/**
 * rb-checks.js — the instant review: explainable rules, each pointing at the
 * field to fix. Runs on every change, costs nothing, needs no network.
 *
 * Replaces the old "ATS score" as the headline number: that one gave an EMPTY
 * resume 48 % and judged Russian bullets by English action verbs (audit
 * 2026-10-09). A rule here either applies to this resume or stays out of the
 * score; the score is the weighted share of applicable rules that pass.
 *
 * The AI review (rb-ai, task 'review') is the deep pass on top of this one.
 *
 * Pure: reads the model, returns data. globalThis.RbChecks
 */
(function () {
    'use strict';

    var W = { high: 3, medium: 2, low: 1 };
    var READY = 85;   /* the «ready to submit» line, shared by the meter and the verdict */

    /* Words recruiters skip — as the evidence-free first words of a summary.
       hh.ru measured «целеустремлённость, трудолюбие» in about half of all
       resumes (hh.ru/article/302530). Lower-case stems. */
    var CLICHES = {
        ru: ['коммуникабельн', 'стрессоустойчив', 'ответственн', 'отвественн', 'целеустремл', 'пунктуальн', 'исполнительн', 'обучаем', 'трудолюбив', 'нацелен на результат', 'командный игрок', 'активная жизненная позиция', 'без вредных привычек'],
        uk: ['комунікабельн', 'стресостійк', 'відповідальн', 'цілеспрямован', 'пунктуальн', 'навчаєм', 'працьовит'],
        en: ['hard-working', 'hardworking', 'team player', 'detail-oriented', 'results-driven', 'go-getter', 'self-starter', 'think outside the box', 'dynamic', 'motivated', 'passionate', 'synergy'],
        de: ['teamfähig', 'belastbar', 'zuverlässig', 'motiviert', 'flexibel', 'kommunikationsstark'],
        es: ['proactivo', 'trabajo en equipo', 'responsable', 'dinámico', 'motivado'],
        fr: ['dynamique', 'motivé', 'esprit d’équipe', "esprit d'équipe", 'rigoureux', 'polyvalent'],
        pt: ['proativo', 'trabalho em equipe', 'dinâmico', 'responsável', 'motivado'],
        id: ['pekerja keras', 'bertanggung jawab', 'mampu bekerja dalam tim', 'disiplin'],
        ko: ['성실', '책임감', '열정', '팀워크'],
        ar: ['مجتهد', 'العمل الجماعي', 'متحمس', 'مسؤول']
    };
    var DUTY_START = /^\s*(обязанности|отвечал(а)? за|ответственн(ый|ая) за|в мои обязанности|responsible for|was responsible|duties|tasks included|aufgaben|zuständig für|responsable de|responsável por|tanggung jawab)/i;
    var FIRST_PERSON = /(^|[\s(«"])(я|мной|мне|i|my|me|ich|mein|yo|je|eu|saya)(?=[\s,.;:!?)»"]|$)/i;
    var SILLY_MAIL = /(kot(ik|enok)|zaik|krasotk|sexy|baby|angel|princes|kiss|lov(e|ely)|cute|sweet|devil|killer|crazy|boss|xxx|kitty|bunny|sunny|malysh)/i;

    function txt(v) { return String(v == null ? '' : v); }
    function has(v) { return txt(v).trim().length > 0; }
    function ym(v) {
        var m = txt(v).match(/^(\d{4})(?:-(\d{1,2}))?/);
        return m ? (+m[1]) * 12 + ((+m[2] || 1) - 1) : null;
    }
    function lines(v) { return txt(v).split('\n').map(function (l) { return l.trim(); }).filter(Boolean); }

    /**
     * @return {{score:number, issues:Array, passed:number, total:number}}
     *   issue: {id, sev:'high'|'medium'|'low', pass:boolean, key, vars, pane, field, idx}
     */
    function run(model, ctx) {
        ctx = ctx || {};
        var s = model, p = s.personal || {};
        var lang = ctx.lang || (s.meta && s.meta.lang) || 'en';
        var conv = ctx.conventions || null;    /* RbConventions profile for the region */
        var out = [];
        function rule(id, sev, ok, key, extra) {
            out.push(Object.assign({ id: id, sev: sev, pass: !!ok, key: key }, extra || {}));
        }

        if (s.meta && s.meta.sample) rule('sample', 'high', false, 'chk_sample', { pane: 'personal', field: 'name' });
        rule('name', 'high', has(p.name), 'chk_name', { pane: 'personal', field: 'name' });
        rule('title', 'medium', has(p.title), 'chk_title', { pane: 'personal', field: 'title' });
        rule('contact', 'high', has(p.email) || has(p.phone), 'chk_contact', { pane: 'personal', field: has(p.email) ? 'phone' : 'email' });
        if (has(p.email)) {
            rule('mail', 'low', !SILLY_MAIL.test(txt(p.email).split('@')[0]), 'chk_mail', { pane: 'personal', field: 'email' });
        }

        var sum = txt(s.summary).trim();
        rule('summary', 'medium', sum.length > 0, 'chk_summary', { pane: 'summary', field: 'summary' });
        if (sum.length) {
            rule('summary_len', 'low', sum.length >= 120 && sum.length <= 650, sum.length < 120 ? 'chk_summary_short' : 'chk_summary_long', { pane: 'summary', field: 'summary' });
            var low = sum.toLowerCase();
            var list = (CLICHES[lang] || []).concat(lang === 'en' ? [] : CLICHES.en);
            var hits = list.filter(function (w) { return low.indexOf(w) > -1; });
            rule('cliche', 'medium', hits.length < 2, 'chk_cliche', { pane: 'summary', field: 'summary', vars: { words: hits.slice(0, 3).join(', ') } });
        }

        var exp = s.experience || [];
        var hasWork = exp.length || (s.projects || []).length || (s.volunteer || []).length;
        rule('experience', 'high', hasWork, 'chk_experience', { pane: 'experience' });

        var numbers = false, duties = null, firstPerson = null, longJob = null, noDates = null, noEnd = null;
        exp.forEach(function (e, i) {
            var body = txt(e.description) + '\n' + txt(e.achievements);
            if (/\d/.test(body.replace(/\b(19|20)\d{2}\b/g, ''))) numbers = true;
            if (duties === null && lines(body).some(function (l) { return DUTY_START.test(l); })) duties = i;
            if (firstPerson === null && lines(body).some(function (l) { return FIRST_PERSON.test(l); })) firstPerson = i;
            if (longJob === null && lines(body).length > 10) longJob = i;
            if (noDates === null && !has(e.startDate)) noDates = i;
            if (noEnd === null && has(e.startDate) && !e.current && !has(e.endDate)) noEnd = i;
        });
        if (exp.length) {
            rule('dates', 'medium', noDates === null, 'chk_dates', { pane: 'experience', idx: noDates, field: 'startDate' });
            rule('end', 'medium', noEnd === null, 'chk_end', { pane: 'experience', idx: noEnd, field: 'endDate', vars: { job: noEnd !== null ? txt(exp[noEnd].position || exp[noEnd].company) : '' } });
            rule('numbers', 'medium', numbers, 'chk_numbers', { pane: 'experience', idx: 0, field: 'achievements' });
            rule('duties', 'low', duties === null, 'chk_duties', { pane: 'experience', idx: duties, field: 'description' });
            rule('first_person', 'low', firstPerson === null, 'chk_first_person', { pane: 'experience', idx: firstPerson, field: 'description' });
            rule('long_job', 'low', longJob === null, 'chk_long_job', { pane: 'experience', idx: longJob, field: 'description' });

            /* gaps over 6 months between jobs, newest first */
            var spans = exp.map(function (e) {
                return { a: ym(e.startDate), b: e.current ? ym(new Date().toISOString().slice(0, 7)) : ym(e.endDate) };
            }).filter(function (x) { return x.a !== null && x.b !== null; }).sort(function (x, y) { return x.a - y.a; });
            var gap = 0;
            for (var i = 1; i < spans.length; i++) gap = Math.max(gap, spans[i].a - spans[i - 1].b);
            if (spans.length > 1) rule('gap', 'low', gap <= 6, 'chk_gap', { pane: 'summary', field: 'summary', vars: { months: gap } });
        }

        var ph = globalThis.RbState.placeholders(s);
        rule('placeholders', 'high', ph.length === 0, 'chk_placeholders', { vars: { n: ph.length, first: ph[0] || '' }, pane: firstPlaceholderPane(s) });

        var skills = (s.skills || []).filter(function (k) { return has(k.name); }).length;
        rule('skills', 'medium', skills >= 4, 'chk_skills', { pane: 'skills', vars: { n: skills } });
        if (skills > 25) rule('skills_many', 'low', false, 'chk_skills_many', { pane: 'skills', vars: { n: skills } });
        rule('education', 'low', (s.education || []).length > 0, 'chk_education', { pane: 'education' });
        rule('languages', 'low', (s.languages || []).length > 0, 'chk_languages', { pane: 'languages' });

        if (ctx.pages) {
            var max = conv && conv.length && conv.length.max ? conv.length.max : 2;
            rule('length', 'medium', ctx.pages <= max, 'chk_length', { pane: 'design', vars: { pages: ctx.pages, max: max } });
        }
        if (conv && conv.fields) {
            var c = s.cultural || {};
            if (conv.photo === 'off' && c.photo && has(p.photo)) rule('photo_off', 'medium', false, 'chk_photo_off', { pane: 'design' });
            ['dob', 'marital', 'religion', 'gender'].forEach(function (f) {
                if (conv.fields[f] === 'off' && c[f] && has(p[f])) rule('field_off_' + f, 'medium', false, 'chk_field_off', { pane: 'design', vars: { field: f } });
            });
        }

        var total = 0, got = 0;
        out.forEach(function (r) { total += W[r.sev]; if (r.pass) got += W[r.sev]; });
        var score = total ? Math.round(got / total * 100) : 0;
        if (globalThis.RbState.isEmpty(s)) score = 0;
        /* An open high-severity rule (untouched example, unfilled «[N%]», no name)
           means the resume cannot be sent yet, whatever the weighted share says:
           an untouched example scored 92 under «ready to submit». The score stays
           below the ready line, so the meter and the verdict agree. */
        var blocked = out.some(function (r) { return !r.pass && r.sev === 'high'; });
        if (blocked) score = Math.min(score, READY - 1);
        var band = score >= READY ? 'ready' : score >= 60 ? 'almost' : 'work';
        var order = { high: 0, medium: 1, low: 2 };
        out.sort(function (a, b) { return (a.pass - b.pass) || (order[a.sev] - order[b.sev]); });
        return { score: score, band: band, issues: out, failed: out.filter(function (r) { return !r.pass; }).length };
    }

    function firstPlaceholderPane(s) {
        var re = /\[[^\]\n]{1,80}\]/;
        if (re.test(txt(s.summary))) return 'summary';
        var lists = ['experience', 'projects', 'volunteer', 'education', 'awards'];
        for (var i = 0; i < lists.length; i++) {
            if ((s[lists[i]] || []).some(function (e) { return Object.keys(e).some(function (k) { return re.test(txt(e[k])); }); })) return lists[i];
        }
        return 'experience';
    }

    /* Per-section state for the rail: empty | partial | done. */
    function sectionState(model, sec) {
        var p = model.personal || {};
        switch (sec) {
            case 'personal':
                if (!has(p.name) && !has(p.title) && !has(p.email) && !has(p.phone)) return 'empty';
                return has(p.name) && has(p.title) && (has(p.email) || has(p.phone)) ? 'done' : 'partial';
            case 'summary':
                var n = txt(model.summary).trim().length;
                return n === 0 ? 'empty' : (n >= 120 ? 'done' : 'partial');
            case 'experience':
                var ex = model.experience || [];
                if (!ex.length) return 'empty';
                return ex.every(function (e) { return has(e.position) && has(e.startDate) && (has(e.description) || has(e.achievements)); }) ? 'done' : 'partial';
            case 'skills':
                var k = (model.skills || []).length;
                return k === 0 ? 'empty' : (k >= 4 ? 'done' : 'partial');
            default:
                if (Array.isArray(model[sec])) return model[sec].length ? 'done' : 'empty';
                return 'empty';
        }
    }

    globalThis.RbChecks = { run: run, sectionState: sectionState, CLICHES: CLICHES };
})();
